import { Outlet, Link, createRootRouteWithContext } from "@tanstack/react-router";
import { QueryClientProvider, type QueryClient } from "@tanstack/react-query";
import { Toaster } from "sonner";
import { AuthProvider } from "@/lib/auth-context";
import { NotificationProvider } from "@/lib/notification-context";
import { WishlistProvider } from "@/lib/wishlist-context";
import { ScrollPauseProvider } from "@/lib/scroll-pause-context";
import { PWAProvider } from "@/lib/pwa";
import { PWAPrompts } from "@/components/pwa-prompts";

interface RouterContext {
  queryClient: QueryClient;
}

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-ink">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-ink">Page not found</h2>
        <p className="mt-2 text-sm text-ink-muted">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
});

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  return (
    <QueryClientProvider client={queryClient}>
      <PWAProvider>
        <AuthProvider>
          <WishlistProvider>
            <NotificationProvider>
              <ScrollPauseProvider>
                {/*
                  The first thing in the tab order, and visible only once it has
                  focus. Without it every keyboard visitor tabs through the whole
                  header — logo, four nav links, notifications, profile — before
                  reaching a single search result, on every page.
                */}
                <a
                  href="#main-content"
                  className="sr-only rounded-lg bg-primary px-4 py-2 text-sm font-bold text-primary-foreground focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-[100]"
                >
                  Skip to main content
                </a>
                <Outlet />
              </ScrollPauseProvider>
              <PWAPrompts />
              <Toaster richColors position="top-center" />
            </NotificationProvider>
          </WishlistProvider>
        </AuthProvider>
      </PWAProvider>
    </QueryClientProvider>
  );
}
